import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, setAuthToken } from '../lib/api';
import { queryKeys } from '../lib/query-client';
import type {
  CheckDto, ItemDto, SongDto, AreaDto, LocationDto, EntranceDto,
  TrickDto, TrickDifficultyDto, TrackingSessionDto, TrackerTemplateDto,
  EraDto, PathGoalDto, AuthResponse,
  CreateSessionRequest, SyncSessionRequest,
  CreateTemplateRequest, UpdateTemplateRequest,
} from './types';

// ==================== Reference Data ====================

export function useChecks() {
  return useQuery({
    queryKey: queryKeys.checks.all,
    queryFn: () => api.get('checks').json<CheckDto[]>(),
  });
}

export function useChecksByLocation(locationId: number) {
  return useQuery({
    queryKey: queryKeys.checks.byLocation(locationId),
    queryFn: () => api.get(`checks/by-location/${locationId}`).json<CheckDto[]>(),
    enabled: locationId > 0,
  });
}

export function useItems() {
  return useQuery({
    queryKey: queryKeys.items.all,
    queryFn: () => api.get('items').json<ItemDto[]>(),
  });
}

export function useSongs() {
  return useQuery({
    queryKey: queryKeys.items.songs,
    queryFn: () => api.get('items/songs').json<SongDto[]>(),
  });
}

export function useAreas() {
  return useQuery({
    queryKey: queryKeys.locations.areas,
    queryFn: () => api.get('locations/areas').json<AreaDto[]>(),
  });
}

export function useLocationsByArea(areaId: number) {
  return useQuery({
    queryKey: queryKeys.locations.byArea(areaId),
    queryFn: () => api.get(`locations/by-area/${areaId}`).json<LocationDto[]>(),
    enabled: areaId > 0,
  });
}

export function useEntrances() {
  return useQuery({
    queryKey: queryKeys.entrances.all,
    queryFn: () => api.get('entrances').json<EntranceDto[]>(),
  });
}

export function useEras() {
  return useQuery({
    queryKey: queryKeys.referenceData.eras,
    queryFn: () => api.get('referencedata/eras').json<EraDto[]>(),
  });
}

export function usePathGoals() {
  return useQuery({
    queryKey: queryKeys.referenceData.pathGoals,
    queryFn: () => api.get('referencedata/path-goals').json<PathGoalDto[]>(),
  });
}

// ==================== Tricks ====================

export function useTricks() {
  return useQuery({
    queryKey: queryKeys.tricks.all,
    queryFn: () => api.get('tricks').json<TrickDto[]>(),
  });
}

export function useTrickById(id: number) {
  return useQuery({
    queryKey: queryKeys.tricks.byId(id),
    queryFn: () => api.get(`tricks/${id}`).json<TrickDto>(),
    enabled: id > 0,
  });
}

export function useTrickDifficulties() {
  return useQuery({
    queryKey: queryKeys.tricks.difficulties,
    queryFn: () => api.get('tricks/difficulties').json<TrickDifficultyDto[]>(),
  });
}

export function useToggleFavoriteTrick() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (trickLinkId: number) =>
      api.post(`tricks/links/${trickLinkId}/favorite`).json<{ isFavorite: boolean }>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tricks.all });
    },
  });
}

// ==================== Sessions ====================

export function useSessionByCode(code: string) {
  return useQuery({
    queryKey: queryKeys.sessions.byCode(code),
    queryFn: () => api.get(`sessions/${code}`).json<TrackingSessionDto>(),
    enabled: !!code,
  });
}

export function useMySessions() {
  return useQuery({
    queryKey: queryKeys.sessions.mine,
    queryFn: () => api.get('sessions/my').json<TrackingSessionDto[]>(),
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSessionRequest) =>
      api.post('sessions', { json: data }).json<TrackingSessionDto>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.mine });
    },
  });
}

export function useSyncSession() {
  return useMutation({
    mutationFn: ({ code, data }: { code: string; data: SyncSessionRequest }) =>
      api.put(`sessions/${code}/sync`, { json: data }).json<TrackingSessionDto>(),
  });
}

// ==================== Templates ====================

export function useTemplates() {
  return useQuery({
    queryKey: queryKeys.templates.all,
    queryFn: () => api.get('templates').json<TrackerTemplateDto[]>(),
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTemplateRequest) =>
      api.post('templates', { json: data }).json<TrackerTemplateDto>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTemplateRequest }) =>
      api.put(`templates/${id}`, { json: data }).json<TrackerTemplateDto>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
    },
  });
}

// ==================== Auth ====================

export function useLogin() {
  return useMutation({
    mutationFn: (data: { userName: string; password: string }) =>
      api.post('auth/login', { json: data }).json<AuthResponse>(),
    onSuccess: (data) => {
      setAuthToken(data.token);
      localStorage.setItem('tracker_user', JSON.stringify({
        userId: data.userId,
        userName: data.userName,
      }));
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (data: { userName: string; email: string; password: string }) =>
      api.post('auth/register', { json: data }).json<AuthResponse>(),
    onSuccess: (data) => {
      setAuthToken(data.token);
      localStorage.setItem('tracker_user', JSON.stringify({
        userId: data.userId,
        userName: data.userName,
      }));
    },
  });
}
